import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Button, Checkbox, FormControlLabel, Link, MenuItem, Skeleton,
  Stack, Tab, Tabs, TextField, Typography,
} from "@mui/material";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LogoutIcon from "@mui/icons-material/Logout";
import GavelIcon from "@mui/icons-material/Gavel";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { C, Pill, Tarjeta, fecha, num } from "../oece/oeceUi";
import {
  getNotifRows, getNotifStats, NotifRow, NotifStats,
} from "../services/notificaciones.service";
import { LeadsTab } from "./LeadsTab";

/* Color de la etiqueta según el tipo de expediente (como en dashboard.py) */
function tonoTipo(t: string | null): "red" | "blue" | "gray" {
  const s = (t || "").toLowerCase();
  if (s.includes("sancion")) return "red";
  if (s.includes("apelacion")) return "blue";
  return "gray";
}

const Kpi = ({
  icono, valor, label, color, bg, destacado,
}: {
  icono: React.ReactNode; valor?: number; label: string;
  color: string; bg: string; destacado?: boolean;
}) => (
  <Tarjeta
    sx={{
      p: 2.25, display: "flex", gap: 1.75, alignItems: "flex-start",
      ...(destacado ? { boxShadow: `inset 0 0 0 1.5px ${C.red}` } : {}),
    }}
  >
    <Box sx={{ width: 42, height: 42, borderRadius: 2.5, bgcolor: bg, color, display: "grid", placeItems: "center", flexShrink: 0 }}>
      {icono}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      {valor === undefined ? (
        <Skeleton width={80} height={34} />
      ) : (
        <Typography sx={{ fontSize: 26, fontWeight: 800, color: destacado ? C.red : C.navy, lineHeight: 1.2 }}>
          {num(valor)}
        </Typography>
      )}
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>{label}</Typography>
    </Box>
  </Tarjeta>
);

/* Tarjeta con barras horizontales (por tipo / por etapa) */
const Barras = ({
  titulo, datos, etiqueta, colorBarra,
}: {
  titulo: string;
  datos?: { n: number; [k: string]: string | number }[];
  etiqueta: string;
  colorBarra?: (nombre: string) => string;
}) => {
  const max = Math.max(1, ...(datos?.map((d) => d.n) ?? [1]));
  return (
    <Tarjeta sx={{ p: 2.25 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.navy, mb: 1.5 }}>{titulo}</Typography>
      {!datos && [0, 1, 2, 3].map((i) => <Skeleton key={i} height={26} />)}
      {datos?.map((d) => {
        const nombre = String(d[etiqueta]);
        return (
          <Stack key={nombre} direction="row" alignItems="center" spacing={1.5} sx={{ my: 0.9 }}>
            <Typography sx={{ flex: "0 0 180px", fontSize: 12.5, color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={nombre}>
              {nombre}
            </Typography>
            <Box sx={{ flex: 1, height: 14, bgcolor: "#edf0f5", borderRadius: 2, overflow: "hidden" }}>
              <Box sx={{ height: "100%", width: `${(d.n / max) * 100}%`, bgcolor: colorBarra?.(nombre) ?? C.navy2, borderRadius: 2 }} />
            </Box>
            <Typography sx={{ flex: "0 0 48px", textAlign: "right", fontSize: 12.5, fontWeight: 700, color: C.navy }}>
              {num(d.n)}
            </Typography>
          </Stack>
        );
      })}
    </Tarjeta>
  );
};

const NotificacionesPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<NotifStats | null>(null);
  const [rows, setRows] = useState<NotifRow[] | null>(null);
  const [soloInicio, setSoloInicio] = useState(true);
  const [tipo, setTipo] = useState("");
  const [etapa, setEtapa] = useState("");
  const [q, setQ] = useState("");
  const [actualizado, setActualizado] = useState<string>("");
  const [vista, setVista] = useState<"leads" | "todas">("leads");
  const rol = localStorage.getItem("rol");
  const esRolNotif = rol === "NOTIFICACIONES";
  // El abogado solo ve a los que ya respondieron: sin stats ni tabla global,
  // solo la pestaña de leads y únicamente la nota es editable (ver backend).
  const esAbogado = rol === "NOTIFICA_ABOGADO";
  const debounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const cargarStats = useCallback(() => {
    getNotifStats()
      .then((r) => {
        setStats(r.data);
        setActualizado(new Date().toLocaleTimeString("es-PE"));
      })
      .catch(() => {});
  }, []);

  const cargarRows = useCallback(() => {
    getNotifRows({ inicio: soloInicio, tipo, etapa, q })
      .then((r) => setRows(r.data))
      .catch(() => {});
  }, [soloInicio, tipo, etapa, q]);

  // Carga + auto-refresh cada 20 s (como el panel original). El abogado no pide
  // stats ni la tabla global (el backend se los bloquea).
  useEffect(() => {
    if (esAbogado) return;
    cargarStats();
    const t = setInterval(cargarStats, 20000);
    return () => clearInterval(t);
  }, [cargarStats, esAbogado]);

  useEffect(() => {
    if (esAbogado) return;
    cargarRows();
    const t = setInterval(cargarRows, 20000);
    return () => clearInterval(t);
  }, [cargarRows, esAbogado]);

  // El buscador espera 300 ms tras dejar de teclear
  const onBuscar = (v: string) => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => setQ(v), 300);
  };

  const apelaciones = useMemo(
    () => stats?.por_tipo.find((x) => /apelacion/i.test(x.tipo))?.n ?? 0,
    [stats],
  );

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const colorTipo = (n: string) =>
    tonoTipo(n) === "red" ? C.red : tonoTipo(n) === "blue" ? C.blue : C.muted;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: C.bg }}>
      {/* ── HERO ── */}
      <Box
        sx={{
          background: `radial-gradient(1200px 400px at 85% -10%, rgba(200,162,74,.22), transparent 60%),
                       linear-gradient(135deg, ${C.navy} 0%, ${C.navy2} 100%)`,
          color: "#fff", px: { xs: 2, md: 5 }, pt: 2.5, pb: 11,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
          {esRolNotif || esAbogado ? <span /> : (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/")}
              sx={{ color: "rgba(255,255,255,.85)", textTransform: "none", "&:hover": { bgcolor: "rgba(255,255,255,.08)" } }}
            >
              Volver al panel
            </Button>
          )}
          <Button
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ color: "rgba(255,255,255,.85)", textTransform: "none", "&:hover": { bgcolor: "rgba(255,255,255,.08)" } }}
          >
            Salir
          </Button>
        </Stack>

        <Box>
          <Typography sx={{ fontSize: 12, letterSpacing: 2, color: C.gold, fontWeight: 700 }}>
            TRIBUNAL DE CONTRATACIONES · NOTIFICACIONES
          </Typography>
          <Typography sx={{ fontSize: { xs: 28, md: 36 }, fontWeight: 800, lineHeight: 1.15, mt: 0.5 }}>
            {esAbogado ? "Casos que respondieron" : "Notificaciones y decretos"}
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,.72)", mt: 1, maxWidth: 640 }}>
            {esAbogado
              ? "Denunciados que respondieron a la notificación, con su contacto, el plazo de descargos y el PDF del decreto. Puedes añadir notas de seguimiento."
              : <>Decretos del Tribunal de Contrataciones clasificados por tipo de expediente y etapa, con el enlace al PDF de cada uno. {actualizado && `Actualizado ${actualizado}.`}</>}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, md: 5 }, mt: -8, pb: 6 }}>
        {/* ── KPIs y barras: solo para NOTIFICACIONES (data global) ── */}
        {!esAbogado && (<>
        <Box
          sx={{
            display: "grid", gap: 2, mb: 2,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" },
          }}
        >
          <Kpi icono={<GavelIcon />} valor={stats?.inicios} label="Inicios de sanción" color={C.red} bg={C.redSoft} destacado />
          <Kpi icono={<DescriptionOutlinedIcon />} valor={stats?.total} label="Total decretos" color={C.navy2} bg="#e9eef7" />
          <Kpi icono={<CampaignOutlinedIcon />} valor={stats ? apelaciones : undefined} label="Apelaciones" color={C.blue} bg={C.blueSoft} />
          <Kpi icono={<PictureAsPdfOutlinedIcon />} valor={stats?.con_pdf} label="Con PDF" color={C.amber} bg={C.amberSoft} />
        </Box>

        <Box sx={{ display: "grid", gap: 2, mb: 3, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
          <Barras titulo="Por tipo de expediente" datos={stats?.por_tipo} etiqueta="tipo" colorBarra={colorTipo} />
          <Barras titulo="Por etapa" datos={stats?.por_etapa} etiqueta="etapa" />
        </Box>
        </>)}

        {/* ── Pestañas: leads (un inicio de PAS por fila) / todas las notificaciones.
             El abogado solo ve leads (sin pestañas). ── */}
        <Tarjeta sx={{ overflow: "hidden", mb: 2 }}>
          {!esAbogado && (
          <Tabs
            value={vista}
            onChange={(_, v) => setVista(v)}
            sx={{
              px: 2, borderBottom: vista === "leads" ? `1px solid ${C.border}` : "none",
              "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: 14, minHeight: 56, color: C.muted },
              "& .Mui-selected": { color: `${C.navy} !important` },
              "& .MuiTabs-indicator": { bgcolor: C.gold, height: 3, borderRadius: "3px 3px 0 0" },
            }}
          >
            <Tab value="leads" label="Leads · inicios de PAS" icon={<PersonSearchOutlinedIcon />} iconPosition="start" />
            <Tab value="todas" label="Todas las notificaciones" icon={<ListAltOutlinedIcon />} iconPosition="start" />
          </Tabs>
          )}
          {(esAbogado || vista === "leads") && <Box sx={{ p: { xs: 2, md: 3 } }}><LeadsTab esAbogado={esAbogado} /></Box>}
        </Tarjeta>

        {!esAbogado && vista === "todas" && (<>
        {/* ── Filtros ── */}
        <Tarjeta sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} flexWrap="wrap" useFlexGap>
            <FormControlLabel
              control={<Checkbox checked={soloInicio} onChange={(e) => setSoloInicio(e.target.checked)} />}
              label={<Typography sx={{ fontSize: 14, fontWeight: 600 }}>Solo inicios de sanción</Typography>}
            />
            <TextField
              select size="small" label="Tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">Todos los tipos</MenuItem>
              {stats?.por_tipo.map((x) => (
                <MenuItem key={x.tipo} value={x.tipo}>{x.tipo}</MenuItem>
              ))}
            </TextField>
            <TextField
              select size="small" label="Etapa" value={etapa} onChange={(e) => setEtapa(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">Todas las etapas</MenuItem>
              {stats?.por_etapa.map((x) => (
                <MenuItem key={x.etapa} value={x.etapa}>{x.etapa}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small" type="search" placeholder="Buscar expediente, idnotifica o nombre…"
              defaultValue="" onChange={(e) => onBuscar(e.target.value)}
              sx={{ flex: 1, minWidth: 240 }}
            />
          </Stack>
        </Tarjeta>

        {/* ── Tabla ── */}
        <Tarjeta sx={{ overflow: "hidden" }}>
          <Box sx={{ overflowX: "auto" }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", "& th, & td": { px: 1.5, py: 1.25, textAlign: "left", borderBottom: `1px solid ${C.border}`, fontSize: 13.5, verticalAlign: "top" }, "& th": { fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: C.muted, bgcolor: "#f8fafc" } }}>
              <thead>
                <tr>
                  <th>idnotifica</th><th>Inicio</th><th>Tipo</th><th>Etapa</th>
                  <th>Expediente</th><th>Fecha</th><th>Conf.</th><th>PDF</th>
                </tr>
              </thead>
              <tbody>
                {rows === null && [0, 1, 2, 3, 4].map((i) => (
                  <tr key={i}><td colSpan={8}><Skeleton height={22} /></td></tr>
                ))}
                {rows?.length === 0 && (
                  <tr><td colSpan={8}><Typography sx={{ py: 3, textAlign: "center", color: C.muted }}>Sin resultados con estos filtros.</Typography></td></tr>
                )}
                {rows?.map((r) => (
                  <tr key={r.idnotifica}>
                    <td style={{ fontVariantNumeric: "tabular-nums" }}>{r.idnotifica}</td>
                    <td>{r.es_inicio_sancion ? <Pill tono="red">● INICIO</Pill> : <Typography component="span" sx={{ color: C.muted }}>—</Typography>}</td>
                    <td><Pill tono={tonoTipo(r.tipo_expediente)}>{r.tipo_expediente || "—"}</Pill></td>
                    <td>{r.etapa || "—"}</td>
                    <td>{r.n_expediente || <Typography component="span" sx={{ color: C.muted }}>—</Typography>}</td>
                    <td>{fecha(r.fecha_decreto)}</td>
                    <td style={{ color: C.muted }}>{r.confianza || "—"}</td>
                    <td>
                      {r.url_pdf ? (
                        <Link href={r.url_pdf} target="_blank" rel="noopener" sx={{ fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 0.3 }}>
                          ver PDF <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </Link>
                      ) : <Typography component="span" sx={{ color: C.muted }}>—</Typography>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
          <Typography sx={{ p: 1.5, color: C.muted, fontSize: 12 }}>
            {rows ? `${rows.length} fila(s) · máx 500, más recientes primero` : "Cargando…"}
          </Typography>
        </Tarjeta>
        </>)}
      </Box>
    </Box>
  );
};

export default NotificacionesPage;
